import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Autocomplete,
  Box,
  Breadcrumbs,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Collapse,
  Link,
  Pagination,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import ArrowBackOutlined from '@mui/icons-material/ArrowBackOutlined';
import SearchOutlined from '@mui/icons-material/SearchOutlined';
import MagicPageShell from '../../components/magicui/MagicPageShell';
import { inspectRider, getRiderWalletTransactions } from '../../services/adminOpsService';
import ridersService from '../../services/ridersService';

function riderOptionLabel(o) {
  if (!o) return '';
  const name = o.name || o.tmpIngameName || o.username || 'Rider';
  const id = o.employeeID || o._id;
  return id ? `${name} (${id})` : name;
}

export default function RiderWalletInspector() {
  const [searchParams] = useSearchParams();
  const initialQ = searchParams.get('riderId') || searchParams.get('q') || '';
  const [selectedRider, setSelectedRider] = useState(null);
  const [riderOptions, setRiderOptions] = useState([]);
  const [advancedQuery, setAdvancedQuery] = useState(initialQ);
  const [showAdvanced, setShowAdvanced] = useState(Boolean(initialQ));
  const [profile, setProfile] = useState(null);
  const [txs, setTxs] = useState([]);
  const [txPage, setTxPage] = useState(1);
  const [txPages, setTxPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const searchDebounceRef = useRef(null);

  const inspectByParams = async (params, page = 1) => {
    setLoading(true);
    setError('');
    try {
      const data = await inspectRider(params);
      setProfile(data);
      if (data?.identity?._id) {
        setSelectedRider({
          _id: data.identity._id,
          name: data.identity.name,
          employeeID: data.identity.employeeID,
          username: data.identity.username,
        });
      }
      const wallet = await getRiderWalletTransactions(data.identity._id, { page, limit: 15 });
      setTxs(wallet.items || []);
      setTxPages(wallet.pagination?.totalPages || 1);
      setTxPage(page);
    } catch (e) {
      setError(e?.response?.data?.message || 'Search failed');
      setProfile(null);
      setTxs([]);
    } finally {
      setLoading(false);
    }
  };

  const searchRiderOptions = useCallback((q) => {
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    const trimmed = String(q || '').trim();
    if (trimmed.length < 2) {
      setRiderOptions([]);
      return;
    }
    searchDebounceRef.current = setTimeout(async () => {
      try {
        const items = await ridersService.search(trimmed, 15);
        setRiderOptions(items);
      } catch {
        setRiderOptions([]);
      }
    }, 250);
  }, []);

  const inspectSelected = async (rider, page = 1) => {
    if (!rider?._id) return;
    await inspectByParams({ riderId: rider._id }, page);
  };

  const inspectAdvanced = async (page = 1) => {
    const q = advancedQuery.trim();
    if (!q) return;
    const params = /^[a-f0-9]{24}$/i.test(q) ? { riderId: q } : { q };
    await inspectByParams(params, page);
  };

  useEffect(() => {
    if (searchParams.get('riderId') || searchParams.get('q')) {
      inspectAdvanced(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => () => {
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
  }, []);

  return (
    <MagicPageShell title="Rider & wallet inspector" subtitle="Identity, recent jobs, and paginated wallet history.">
      <Breadcrumbs sx={{ mb: 2 }}>
        <Link component={RouterLink} to="/admin/operations" underline="hover" color="inherit">
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <ArrowBackOutlined fontSize="small" />
            <span>Maintenance tools</span>
          </Stack>
        </Link>
        <Typography color="text.primary">Rider inspector</Typography>
      </Breadcrumbs>

      <Card variant="outlined" sx={{ mb: 2 }}>
        <CardContent>
          <Stack spacing={2}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'flex-start' }}>
              <Autocomplete
                sx={{ flex: 1, minWidth: 0 }}
                options={riderOptions}
                getOptionLabel={riderOptionLabel}
                value={selectedRider}
                onChange={(_, rider) => {
                  setSelectedRider(rider);
                  if (rider?._id) inspectSelected(rider, 1);
                }}
                onInputChange={(_, value, reason) => {
                  if (reason === 'input') searchRiderOptions(value);
                }}
                isOptionEqualToValue={(a, b) => String(a?._id) === String(b?._id)}
                filterOptions={(x) => x}
                noOptionsText="Type at least 2 characters"
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Look up rider by name"
                    placeholder="Name, username, or employee ID"
                    fullWidth
                  />
                )}
              />
              <Button
                variant="contained"
                startIcon={<SearchOutlined />}
                onClick={() => inspectSelected(selectedRider, 1)}
                disabled={loading || !selectedRider?._id}
                sx={{ whiteSpace: 'nowrap' }}
              >
                Inspect
              </Button>
            </Stack>

            <Button
              size="small"
              onClick={() => setShowAdvanced((v) => !v)}
              sx={{ alignSelf: 'flex-start', textTransform: 'none' }}
            >
              {showAdvanced ? 'Hide advanced lookup' : 'Advanced: paste ID / Steam / TruckersHub'}
            </Button>

            <Collapse in={showAdvanced}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField
                  label="Paste rider ID, Steam, TruckersHub, or employee ID"
                  value={advancedQuery}
                  onChange={(e) => setAdvancedQuery(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && inspectAdvanced(1)}
                  fullWidth
                  size="small"
                />
                <Button
                  variant="outlined"
                  startIcon={<SearchOutlined />}
                  onClick={() => inspectAdvanced(1)}
                  disabled={loading || !advancedQuery.trim()}
                >
                  Inspect
                </Button>
              </Stack>
            </Collapse>
          </Stack>
        </CardContent>
      </Card>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {loading && <Box sx={{ py: 4, textAlign: 'center' }}><CircularProgress /></Box>}

      {profile && !loading && (
        <Stack spacing={2}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="h6" fontWeight={700}>{profile.identity.name}</Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
                <Chip size="small" label={`Balance: ${profile.identity.balance}`} />
                <Chip size="small" label={`TH: ${profile.identity.truckershubId || '—'}`} />
                <Chip size="small" label={`Steam: ${profile.identity.steamID || '—'}`} />
                <Chip size="small" label={`Emp: ${profile.identity.employeeID || '—'}`} />
                {profile.division && <Chip size="small" color="primary" label={profile.division.name} />}
              </Stack>
              {profile.latestDeliveredFlags && (
                <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                  Latest delivered job #{profile.latestDeliveredFlags.jobID} — statsApplied={String(profile.latestDeliveredFlags.statsApplied)}
                </Typography>
              )}
            </CardContent>
          </Card>

          <Card variant="outlined">
            <CardContent>
              <Typography fontWeight={700} sx={{ mb: 1 }}>Recent jobs</Typography>
              <Stack spacing={0.5}>
                {(profile.recentJobs || []).map((j) => (
                  <Typography key={j.jobID} variant="body2">
                    #{j.jobID} · {j.status} · {j.revenue ?? '—'} · {new Date(j.createdAt).toLocaleString()}
                  </Typography>
                ))}
              </Stack>
            </CardContent>
          </Card>

          <Card variant="outlined">
            <CardContent>
              <Typography fontWeight={700} sx={{ mb: 1 }}>Wallet transactions</Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell>Kind</TableCell>
                    <TableCell>Title</TableCell>
                    <TableCell align="right">Amount</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {txs.map((t) => (
                    <TableRow key={t._id}>
                      <TableCell>{new Date(t.createdAt).toLocaleString()}</TableCell>
                      <TableCell>{t.source?.kind}</TableCell>
                      <TableCell>{t.title}</TableCell>
                      <TableCell align="right">{t.amount}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                <Pagination
                  count={txPages}
                  page={txPage}
                  onChange={(_, p) => {
                    if (selectedRider?._id) inspectSelected(selectedRider, p);
                    else if (advancedQuery.trim()) inspectAdvanced(p);
                  }}
                />
              </Box>
            </CardContent>
          </Card>
        </Stack>
      )}
    </MagicPageShell>
  );
}
